<template>
    <div class="multi-metric-scorecard-template scorecard-template" ref="container">
        <p class="small text-muted mb-2 mx-2">{{ chart_selected.description }}</p>
        <template v-if="chart_selected.data">
            <!-- Special handling for exactly 3 metrics -->
            <template v-if="metricKeys.length === 3 && !isSmall">
                <!-- First row: first two metrics -->
                <div class="row">
                    <div v-for="(key, index) in metricKeys.slice(0, 2)" :key="key" :class="getColumnClass(index)">
                        <div class="metric-item">
                            <div class="metric-icon-section" :class="getMetricColorClass(index)">
                                <img :src="getMetricIcon(index)" :class="['metric-icon', getIconColorClass(index)]">
                            </div>
                            <div class="metric-info-section">
                                <p class="metric-name mb-0">{{ key }}</p>
                                <div class="metric-value-container">
                                    <p class="mb-0">{{ getFormattedValue(key, index) }}</p>
                                    <span class="badge mt-1" v-if="getVariance(key) != null"
                                        :class="{ 'bg-green bg-icon-increase': (getVariance(key) >= 0), 'bg-red bg-icon-decrease': (getVariance(key) < 0)}"
                                    >{{percentage_na(getVariance(key))}}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Second row: third metric full width -->
                <div class="row">
                    <div class="col-12 mb-4">
                        <div class="metric-item">
                            <div class="metric-icon-section" :class="getMetricColorClass(2)">
                                <img :src="getMetricIcon(2)" :class="['metric-icon', getIconColorClass(2)]">
                            </div>
                            <div class="metric-info-section">
                                <p class="metric-name mb-0">{{ metricKeys[2] }}</p>
                                <div class="metric-value-container">
                                    <p class="mb-0">{{ getFormattedValue(metricKeys[2], 2) }}</p>
                                    <span class="badge mt-1" v-if="getVariance(metricKeys[2]) != null"
                                        :class="{ 'bg-green bg-icon-increase': (getVariance(metricKeys[2]) >= 0), 'bg-red bg-icon-decrease': (getVariance(metricKeys[2]) < 0)}"
                                    >{{percentage_na(getVariance(metricKeys[2]))}}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
            <!-- Default layout for all other cases -->
            <div v-else class="row mx-0 metric-items-container">
                <div v-for="(key, index) in metricKeys" :key="key" :class="getColumnClass(index)">
                    <div class="metric-item">
                        <div class="metric-icon-section" :class="getMetricColorClass(index)">
                            <img :src="getMetricIcon(index)" :class="['metric-icon', getIconColorClass(index)]">
                        </div>
                        <div class="metric-info-section">
                            <p class="metric-name mb-0">{{ key }}</p>
                            <div class="metric-value-container">
                                <p class="mb-0">{{ getFormattedValue(key, index) }}</p>
                                <span class="badge mt-1" v-if="getVariance(key) != null"
                                    :class="{ 'bg-green bg-icon-increase': (getVariance(key) >= 0), 'bg-red bg-icon-decrease': (getVariance(key) < 0)}"
                                >{{percentage_na(getVariance(key))}}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <div v-else-if="!if_refreshing" class='d-inline-block text-center'>
            <h1 class="text-secondary mb-1"><i class="fas fa-chart-simple"></i></h1>
            <h4 class="text-secondary mb-2"><em>Press the <strong>Preview</strong> button to render the chart</em></h4>
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import filtersMixin from '../../../../mixins/filters';

export default defineComponent({
    name: 'MultiMetricScorecardTemplate',
    mixins: [filtersMixin],
    props: {
        chart_selected: {
            type: Object,
            required: true
        },
        if_refreshing: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            isSmall: false,
            resizeObserver: null
        };
    },
    computed: {
        metricKeys() {
            if (!this.chart_selected?.data) return [];
            return Object.keys(this.chart_selected.data);
        }
    },
    methods: {
        formatDateValueForDisplay(value) {
            if (value == null || value === '') return null;
            let dateInput = value;
            const str = String(value).replace(/\D/g, '');
            if (str.length === 8) {
                dateInput = `${str.slice(0, 4)}-${str.slice(4, 6)}-${str.slice(6, 8)}`;
            }
            return this.date_long_abbrev(dateInput) || null;
        },
        getFormattedValue(key, index) {
            const data = this.chart_selected.data[key];
            if (!data || data.length === 0) return 'N/A';
            
            const reversedData = [...data].reverse();
            const value = reversedData[0];
            
            const format_type = this.chart_selected?.metric_formats?.[index] || 'number';

            switch (format_type) {
                case 'money':
                    return this.currency_na(value);
                case 'percent':
                    return this.percentage_na(value);
                case 'date':
                    return this.formatDateValueForDisplay(value) ?? this.number_na(value);
                default:
                    return this.number_na(value);
            }
        },
        getVariance(key) {
            const data = this.chart_selected.data[key];
            if (!data || data.length < 2) return null;
            
            const reversedData = [...data].reverse();
            const currentValue = reversedData[0];
            const previousValue = reversedData[1];
            
            if (previousValue != null && previousValue != 0) {
                return (currentValue - previousValue) / previousValue;
            }
            return null;
        },
        getMetricIcon(index) {
            const metric = this.chart_selected?.metrics?.[index];
            const color = metric?.color?.toLowerCase() || '';
            const iconMap = {
                'blue': '/img/icons/avid-icons/metric-icon-donor.svg',
                'yellow': '/img/icons/avid-icons/metric-icon-gifts.svg',
                'green': '/img/icons/avid-icons/metric-icon-revenue.svg',
                'purple': '/img/icons/avid-icons/metric-icon-other.svg'
            };
            return iconMap[color] || '/img/icons/avid-icons/metric-icon-other.svg';
        },
        getMetricColorClass(index) {
            const metric = this.chart_selected?.metrics?.[index];
            return metric?.color ? `passive-${metric.color.toLowerCase()}` : '';
        },
        getIconColorClass(index) {
            const metric = this.chart_selected?.metrics?.[index];
            return metric?.color ? `icon-${metric.color.toLowerCase()}` : '';
        },
        getColumnClass(index) {
            // When size is small, keep the current logic
            if (this.chart_selected?.size === 'small') {
                return this.isSmall ? 'col-12 mb-2' : 'col-md-6 mb-4';
            }
            // Default fallback (for medium or other sizes)
            return this.isSmall ? 'col-12 mb-2' : 'col-md-6 mb-4';
        },
        handleResize(entries) {
            for (let entry of entries) {
                // If container width is less than ~420px, we go 1 column
                // (each metric item would be roughly < 200px + gaps)
                this.isSmall = entry.contentRect.width < 420;
            }
        }
    },
    mounted() {
        this.resizeObserver = new ResizeObserver(this.handleResize);
        if (this.$refs.container) {
            this.resizeObserver.observe(this.$refs.container);
        }
    },
    beforeUnmount() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }
});
</script>
